import { ChevronDownIcon, InfoCircleIcon } from '@icons';
import clsx from 'clsx';
import { useScrollObserver } from 'features/hooks/useScrollObserver';
import { useLanguage } from 'features/hooks/useLanguage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LanguageType } from 'types/enums';
import { Skeleton } from '../component/Skeleton';
import { useGetFormsQuery } from '../data/useGetFormsQuery/useGetFormsQuery';
import { useProjectData } from '../data/useProjectData/useProjectData';
import { PreviewEntryForm } from '../PreviewEntryForm';

export const SelectForm = ({
	expanded,
	onFormSelected,
	selectedFormId,
	seriesName
}: {
	expanded: boolean;
	onFormSelected: (formId: string) => void;
	selectedFormId: string | null;
	seriesName?: string;
}) => {
	const params = useParams();
	const projectId = params.projectId as string;

	const getFormsQuery = useGetFormsQuery({
		projectId,
		seriesName
	});

	const activeFormRef = useRef<HTMLButtonElement>(null);
	const hasScrolledInitially = useRef(false);
	useEffect(() => {
		if (activeFormRef.current && !hasScrolledInitially.current) {
			activeFormRef.current.scrollIntoView({
				block: 'nearest',
				inline: 'start'
			});

			hasScrolledInitially.current = true;
		}
	}, [activeFormRef.current]);

	const { containerRef, canScrollLeft, canScrollRight, scrollLeft, scrollRight } =
		useScrollObserver<HTMLUListElement>();

	if (!getFormsQuery.data) {
		return null;
	}

	if (getFormsQuery.data.activeForms.length < 2) {
		return null;
	}

	if (!expanded) {
		return null;
	}

	return (
		<div className="bg-gray-300 flex flex-col items-center -mt-20 mb-20 py-10">
			<div className="flex flex-row gap-4">
				<button
					onClick={scrollLeft}
					className={clsx(
						'self-center p-2 bg-white rounded-full shadow-md hover:bg-gray-50',
						!canScrollLeft && 'opacity-0'
					)}
					aria-label="Scroll left"
				>
					<ChevronDownIcon className="text-icon-base rotate-90" />
				</button>

				<ul
					className="flex gap-4 px-4 md:max-w-[768px] lg:max-w-[1024px] xl:max-w-[1200px] overflow-x-scroll no-scrollbar"
					ref={containerRef}
				>
					{getFormsQuery.data.activeForms.map(form => {
						const isSelected = form.formId.toString() === selectedFormId;

						return (
							<li key={form.formId} className="flex flex-col gap-4">
								<button
									className="flex flex-col"
									onClick={() => onFormSelected(form.formId.toString())}
									ref={isSelected ? activeFormRef : null}
								>
									<div
										className={clsx(
											'border-2 w-[120px] h-[151px] rounded-md p-4 bg-white shadow-normal overflow-hidden ',
											isSelected && 'border-primary-700'
										)}
									>
										<FormPreview
											formId={form.formId.toString()}
											seriesName={seriesName}
										/>
									</div>

									<p className="mt-4 text-ellipsis text-sm overflow-hidden whitespace-nowrap max-w-[120px]">
										{form.formDefinition.formName}
									</p>
								</button>
							</li>
						);
					})}
				</ul>

				<button
					onClick={scrollRight}
					className={clsx(
						'self-center p-2 bg-white rounded-full shadow-md hover:bg-gray-50',
						!canScrollRight && 'opacity-0'
					)}
					aria-label="Scroll right"
				>
					<ChevronDownIcon className="text-icon-base -rotate-90" />
				</button>
			</div>
		</div>
	);
};

export const SelectFormForSurvey = ({
	onFormSelected,
	selectedFormId,
	seriesName
}: {
	onFormSelected: (formId: string) => void;
	selectedFormId: string | null;
	seriesName?: string;
}) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const params = useParams();
	const projectId = params.projectId as string;

	const getFormsQuery = useGetFormsQuery({
		projectId,
		seriesName
	});

	const activeFormRef = useRef<HTMLButtonElement>(null);
	const hasScrolledInitially = useRef(false);
	useEffect(() => {
		if (
			activeFormRef.current &&
			!hasScrolledInitially.current &&
			getFormsQuery.data?.activeForms.length
		) {
			activeFormRef.current.scrollIntoView({
				block: 'nearest',
				inline: 'start'
			});

			hasScrolledInitially.current = true;
		}
	}, [getFormsQuery.data?.activeForms.length]);

	const { containerRef, canScrollLeft, canScrollRight, scrollLeft, scrollRight } =
		useScrollObserver<HTMLUListElement>();

	const needsScrolling = canScrollLeft || canScrollRight;

	if (getFormsQuery.isLoading) {
		return null;
	}

	if (!getFormsQuery.data || getFormsQuery.data.activeForms.length === 0) {
		return (
			<div className="w-full">
				<div className="flex gap-2 items-center bg-warning-200/25 rounded-md overflow-hidden">
					<div className="bg-warning-200 px-4 self-stretch flex items-center">
						<InfoCircleIcon className="size-6 text-warning-800" />
					</div>
					<p className="text-base font-semibold text-warning-800 py-4 px-1">
						{dictionary.formsWarning}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center w-full">
			<div className="flex flex-row gap-4 w-full max-w-full">
				<button
					onClick={scrollLeft}
					className={clsx(
						'self-center p-2 bg-white rounded-full shadow-md hover:bg-gray-50 flex-shrink-0',
						!canScrollLeft && 'opacity-0'
					)}
					aria-label="Scroll left"
				>
					<ChevronDownIcon className="text-icon-base rotate-90" />
				</button>

				<ul
					className={clsx(
						'flex gap-4 px-4 overflow-x-auto no-scrollbar flex-1',
						!needsScrolling && 'justify-center'
					)}
					ref={containerRef}
				>
					{getFormsQuery.data.activeForms.map(form => {
						const isSelected = form.formId.toString() === selectedFormId;

						return (
							<li key={form.formId} className="flex flex-col gap-4 flex-shrink-0">
								<button
									className="flex flex-col"
									onClick={() => onFormSelected(form.formId.toString())}
									ref={isSelected ? activeFormRef : null}
								>
									<div
										className={clsx(
											'border-2 w-[120px] h-[151px] rounded-md p-4 bg-white shadow-normal overflow-hidden ',
											isSelected && 'border-primary-700 cursor-default'
										)}
									>
										<FormPreview
											formId={form.formId.toString()}
											seriesName={seriesName}
										/>
									</div>

									<p className="mt-4 text-ellipsis text-sm overflow-hidden whitespace-nowrap max-w-[120px]">
										{form.formDefinition.formName}
									</p>
								</button>
							</li>
						);
					})}
				</ul>

				<button
					onClick={scrollRight}
					className={clsx(
						'self-center p-2 bg-white rounded-full shadow-md hover:bg-gray-50 flex-shrink-0',
						!canScrollRight && 'opacity-0'
					)}
					aria-label="Scroll right"
				>
					<ChevronDownIcon className="text-icon-base -rotate-90" />
				</button>
			</div>
		</div>
	);
};

const FormPreview = ({ formId, seriesName }: { formId: string; seriesName?: string }) => {
	const params = useParams();
	const projectId = params.projectId as string;

	const projectData = useProjectData({
		projectId,
		selectedFormId: formId,
		seriesName,
		allowPotentiallyStaleData: true
	});

	// For large projects with many forms rendering all items caused performance issues
	const firstTwentyItems = useMemo(() => {
		if (!projectData.data) {
			return [];
		}

		return [...projectData.data.formItems].splice(0, 20);
	}, [projectData.data]);

	if (!projectData.data) {
		return (
			<div className="grid grid-cols-2 gap-1">
				<div className="col-span-2">
					<Skeleton className="w-1/2 h-[6px] rounded-sm" />
				</div>

				{Array.from({ length: 6 }).map((_, index) => (
					<div key={index} className="flex flex-col gap-[1px]">
						<Skeleton className="h-[3px] w-1/3 rounded-sm" />
						<Skeleton className="h-[6px] w-full rounded-sm" />
					</div>
				))}
			</div>
		);
	}

	return (
		<PreviewEntryForm
			projectData={{
				...projectData.data,
				formItems: firstTwentyItems
			}}
		/>
	);
};

export const useFormSelector = ({
	projectId,
	seriesName
}: {
	projectId: string;
	seriesName?: string;
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const formId = searchParams.get('formId');
	const setSelectedFormId = (updatedFormId: string) => {
		setSearchParams({ formId: updatedFormId }, { replace: true });
	};

	const getFormsQuery = useGetFormsQuery({
		projectId,
		seriesName
	});
	const [shouldShowFormSelector, setShouldShowFormSelector] = useState<boolean>(false);
	useEffect(() => {
		if (getFormsQuery.data && getFormsQuery.data.activeForms.length >= 1) {
			if (!formId) {
				setSelectedFormId(getFormsQuery.data.activeForms[0].formId.toString());
			}

			if (getFormsQuery.data.activeForms.length > 1) {
				setShouldShowFormSelector(true);
			}
		}
	}, [getFormsQuery.data]);

	return {
		shouldShowFormSelector,
		toggleShowFormSelector: () => setShouldShowFormSelector(prev => !prev),
		formId,
		setSelectedFormId,
		getFormsQueryIsLoading: getFormsQuery.isLoading,

		defaultFormId: getFormsQuery.data?.activeForms[0]?.formId.toString() || null
	};
};

const DICTIONARY = {
	[LanguageType.English]: {
		formsWarning:
			'You need a form to send a survey. If a form exists but is not active, please activate it.'
	},
	[LanguageType.Norwegian]: {
		formsWarning:
			'Du må ha et skjema for å sende en undersøkelse. Hvis et skjema finnes men ikke er aktivt, må det aktiveres.'
	}
};
